<template>
  <div class="purchase-detail-page">
    <!-- 顶部导航 -->
    <header class="back-header">
      <div class="back-btn" @click="goBack">←</div>
      <h1>{{ $t('wholesaler.purchase.PurchaseDetail.line6_text') }}</h1>
    </header>

    <div class="main-content" v-if="order">
      <!-- 状态卡片 -->
      <div class="status-card" :class="getStatusClass(order.status)">
        <div class="status-label">{{ order.statusLabel }}</div>
        <div class="order-no">{{ order.purchaseNo }}</div>
      </div>

      <!-- 基本信息 -->
      <div class="section">
        <h3 class="section-title">{{ $t('wholesaler.purchase.PurchaseDetail.line18_text') }}</h3>
        <div class="info-grid">
          <div class="info-item">
            <span class="label">{{ $t('wholesaler.purchase.PurchaseDetail.line21_text') }}</span>
            <span class="value">{{ order.supplierName || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="label">{{ $t('wholesaler.purchase.PurchaseDetail.line25_text') }}</span>
            <span class="value">{{ order.warehouseName || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="label">{{ $t('wholesaler.purchase.PurchaseDetail.line29_text') }}</span>
            <span class="value">{{ order.purchaseDate }}</span>
          </div>
          <div class="info-item">
            <span class="label">{{ $t('wholesaler.purchase.PurchaseDetail.line33_text') }}</span>
            <span class="value">{{ formatDate(order.createdAt) }}</span>
          </div>
          <div class="info-item full-width" v-if="order.remark">
            <span class="label">{{ $t('wholesaler.purchase.PurchaseDetail.line37_text') }}</span>
            <span class="value">{{ order.remark }}</span>
          </div>
        </div>
      </div>

      <!-- 商品明细 -->
      <div class="section">
        <h3 class="section-title">{{ $t('wholesaler.purchase.PurchaseDetail.line45_text') }}</h3>
        <div class="items-list">
          <div v-for="item in order.items" :key="item.id" class="item-card">
            <div class="item-header">
              <span class="item-name">{{ item.productName }}</span>
              <span class="item-sku">{{ item.productSku }}</span>
              <span class="tax-label" :class="getTaxClass(item)">{{ getTaxLabel(item) }}</span>
            </div>
            <div class="item-body">
              <div class="item-info">
                <div class="info-row">
                  <span>{{ $t('wholesaler.purchase.PurchaseDetail.quantity') }}: {{ item.quantity }}{{ item.unit }}</span>
                  <span>{{ $t('wholesaler.purchase.PurchaseDetail.unitPrice') }}: ¥{{ formatMoney(item.unitPrice) }}</span>
                </div>
                <div class="info-row" v-if="item.currentStock !== null || item.warehouseName">
                  <span v-if="item.currentStock !== null">{{ $t('wholesaler.purchase.PurchaseDetail.currentStock') }}: {{ item.currentStock }}{{ item.unit }}</span>
                  <span v-if="item.warehouseName">{{ $t('wholesaler.purchase.PurchaseDetail.warehouse') }}: {{ item.warehouseName }}</span>
                </div>
              </div>
              <div class="item-subtotal">
                <template v-if="item.taxType === 'NONE' || !item.taxType">
                  小计: ¥{{ formatMoney(item.subtotal) }}
                </template>
                <template v-else-if="item.taxType === 'INCLUSIVE'">
                  小计: ¥{{ formatMoney(item.subtotal) }}（其中税额: ¥{{ formatMoney(item.taxAmount) }}）
                </template>
                <template v-else-if="item.taxType === 'EXCLUSIVE'">
                  小计: ¥{{ formatMoney(item.subtotal) }}
                  <span class="tax-amount">税额: ¥{{ formatMoney(item.taxAmount) }}</span>
                  <span class="tax-total">含税小计: ¥{{ formatMoney(item.totalWithTax) }}</span>
                </template>
              </div>
            </div>
          </div>
        </div>
        <div class="summary">
          <div class="summary-row">
            <span>{{ $t('wholesaler.purchase.PurchaseDetail.line65_text') }}</span>
            <span>{{ order.totalQuantity }}{{ $t('wholesaler.purchase.PurchaseDetail.unit') }}</span>
          </div>
          <div class="summary-row">
            <span>商品合计</span>
            <span>¥{{ formatMoney(totalSubtotal) }}</span>
          </div>
          <div class="summary-row tax-summary">
            <span>税额合计</span>
            <span>¥{{ formatMoney(totalTaxAmount) }}</span>
          </div>
          <div class="summary-row total">
            <span>总金额（含税）</span>
            <span>¥{{ formatMoney(totalWithTaxAmount) }}</span>
          </div>
        </div>
      </div>

      <!-- 付款信息 -->
      <div class="section">
        <h3 class="section-title">{{ $t('wholesaler.purchase.PurchaseDetail.line77_text') }}</h3>
        <div class="payment-summary">
          <div class="payment-row">
            <span>{{ $t('wholesaler.purchase.PurchaseDetail.line80_text') }}</span>
            <span class="money">¥{{ formatMoney(order.totalAmount) }}</span>
          </div>
          <div class="payment-row">
            <span>{{ $t('wholesaler.purchase.PurchaseDetail.line84_text') }}</span>
            <span class="money" style="color: #ff9800;">¥{{ formatMoney(totalRefundAmount) }}</span>
          </div>
          <div class="payment-row">
            <span>{{ $t('wholesaler.purchase.PurchaseDetail.line88_text') }}</span>
            <span class="money unpaid">¥{{ formatMoney(order.totalAmount - totalRefundAmount) }}</span>
          </div>
          <div class="payment-row">
            <span>{{ $t('wholesaler.purchase.PurchaseDetail.line92_text') }}</span>
            <span class="money paid">¥{{ formatMoney(order.paidAmount) }}</span>
          </div>
          <div class="payment-row">
            <span>{{ $t('wholesaler.purchase.PurchaseDetail.line96_text') }}</span>
            <span :class="getPaymentClass(order.paymentStatus)">{{ getPaymentLabel(order.paymentStatus) }}</span>
          </div>
        </div>

        <!-- 付款记录 -->
        <div class="payment-records" v-if="order.payments && order.payments.length > 0">
          <h4>{{ $t('wholesaler.purchase.PurchaseDetail.line103_text') }}</h4>
          <div v-for="payment in order.payments" :key="payment.id" class="payment-record">
            <div class="record-header">
              <span class="amount">¥{{ formatMoney(payment.paymentAmount) }}</span>
              <span class="method">{{ getMethodLabel(payment.paymentMethod) }}</span>
            </div>
            <div class="record-footer">
              <span>{{ payment.paymentDate }}</span>
              <span v-if="payment.remark">{{ payment.remark }}</span>
            </div>
            <div class="record-proof" v-if="payment.proofUrl">
              <img :src="payment.proofUrl" :alt="$t('wholesaler.purchase.PurchaseDetail.line114_alt')" class="proof-image" @click="previewImage(payment.proofUrl)" />
            </div>
          </div>
        </div>

        <!-- 退款流水 -->
        <div class="refund-records" v-if="order.refunds && order.refunds.length > 0">
          <h4>{{ $t('wholesaler.purchase.PurchaseDetail.line121_text') }}</h4>
          <div v-for="refund in order.refunds" :key="refund.id" class="refund-record">
            <div class="record-header">
              <span class="amount refund-amount">¥{{ formatMoney(refund.amount) }}</span>
              <span class="refund-no">{{ refund.refundNo }}</span>
            </div>
            <div class="record-footer">
              <span>{{ formatDate(refund.refundedAt || refund.createdAt) }}</span>
              <span v-if="refund.operatorNote">{{ refund.operatorNote }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 操作按钮 -->
      <div class="action-bar" v-if="order.status !== 'CANCELLED'">
        <button v-if="order.status === 'DRAFT'" class="action-btn primary" @click="confirmOrder">{{ $t('wholesaler.purchase.PurchaseDetail.line137_text') }}</button>
        <button v-if="order.status === 'CONFIRMED'" class="action-btn success" @click="receiveOrder">{{ $t('wholesaler.purchase.PurchaseDetail.line138_text') }}</button>
        <button v-if="order.paymentStatus !== 'PAID'" class="action-btn warning" @click="openPaymentDialog">{{ $t('wholesaler.purchase.PurchaseDetail.line139_text') }}</button>
        <button v-if="order.status === 'RECEIVED' && order.paidAmount > 0 && !order.hasPendingReturn" class="action-btn after-sale" @click="createReturn">{{ $t('wholesaler.purchase.PurchaseDetail.line140_text') }}</button>
        <button v-if="order.status !== 'RECEIVED'" class="action-btn danger" @click="cancelOrder">{{ $t('wholesaler.purchase.PurchaseDetail.line141_text') }}</button>
      </div>

      <!-- 售后单列表 -->
      <div class="section" v-if="returnOrders && returnOrders.length > 0">
        <h3 class="section-title">{{ $t('wholesaler.purchase.PurchaseDetail.line146_text') }}</h3>
        <div class="return-orders-list">
          <div v-for="ret in returnOrders" :key="ret.id" class="return-order-card">
            <div class="return-header">
              <span class="return-no">{{ ret.returnNo }}</span>
              <span class="return-status" :class="'status-' + ret.status.toLowerCase()">{{ getReturnStatusLabel(ret.status) }}</span>
            </div>
            <div class="return-body">
              <div class="return-info">
                <span>{{ $t('wholesaler.purchase.PurchaseDetail.returnQuantity') }}: {{ ret.totalReturnQuantity }}</span>
                <span>{{ $t('wholesaler.purchase.PurchaseDetail.refundAmount') }}: ¥{{ formatMoney(ret.totalRefundAmount) }}</span>
              </div>
              <div class="return-reason">
                <span>{{ $t('wholesaler.purchase.PurchaseDetail.reason') }}: {{ getReasonLabel(ret.reasonCode) }}</span>
              </div>
              <div class="return-reason" v-if="ret.reasonText">
                <span>{{ $t('wholesaler.purchase.PurchaseDetail.description') }}: {{ ret.reasonText }}</span>
              </div>
              <div class="return-time">
                <span>{{ $t('wholesaler.purchase.PurchaseDetail.applyTime') }}: {{ formatDate(ret.requestedAt) }}</span>
              </div>
              <!-- 审核信息 -->
              <div class="return-review" v-if="ret.reviewedAt">
                <div class="review-info">
                  <span>{{ $t('wholesaler.purchase.PurchaseDetail.reviewStatus') }}: {{ getReturnStatusLabel(ret.status) }}</span>
                </div>
                <div class="review-info" v-if="ret.reviewNote">
                  <span>{{ $t('wholesaler.purchase.PurchaseDetail.reviewNote') }}: {{ ret.reviewNote }}</span>
                </div>
                <div class="review-info">
                  <span>{{ $t('wholesaler.purchase.PurchaseDetail.reviewTime') }}: {{ formatDate(ret.reviewedAt) }}</span>
                </div>
              </div>
              <!-- 凭证图片 -->
              <div class="return-evidence" v-if="getEvidenceUrls(ret.evidenceUrls).length > 0">
                <span class="evidence-label">{{ $t('wholesaler.purchase.PurchaseDetail.line181_text') }}</span>
                <div class="evidence-images">
                  <img 
                    v-for="(url, index) in getEvidenceUrls(ret.evidenceUrls)" 
                    :key="index" 
                    :src="getImageUrl(url)" 
                    class="evidence-thumb"
                    @click="previewImage(url)"
                  />
                </div>
              </div>
            </div>
            <div class="return-actions">
              <button v-if="ret.status === 'PENDING_REVIEW'" class="action-btn primary" @click="approveReturn(ret)">{{ $t('wholesaler.purchase.PurchaseDetail.line194_text') }}</button>
              <button v-if="ret.status === 'PENDING_REVIEW'" class="action-btn danger" @click="rejectReturn(ret)">{{ $t('wholesaler.purchase.PurchaseDetail.line195_text') }}</button>
              <button v-if="ret.status === 'REVIEWED'" class="action-btn success" @click="receiveReturn(ret)">{{ $t('wholesaler.purchase.PurchaseDetail.line196_text') }}</button>
              <button v-if="ret.status !== 'REFUNDED' && !isFullyRefunded(ret)" class="action-btn warning" @click="refundReturn(ret)">{{ $t('wholesaler.purchase.PurchaseDetail.line197_text') }}</button>
              <span v-if="isFullyRefunded(ret)" class="refunded-tag">已收退款</span>
              <button v-if="ret.status !== 'REFUNDED' && ret.status !== 'CANCELLED' && ret.status !== 'REJECTED'" class="action-btn danger" @click="cancelReturn(ret)">{{ $t('wholesaler.purchase.PurchaseDetail.line198_text') }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="loading">{{ $t('common.loading') }}</div>

    <!-- 付款弹窗 -->
    <div v-if="showPaymentDialog" class="modal-overlay modal-show" @click="closePaymentDialog">
      <div class="modal-content" @click.stop>
        <h3>{{ $t('wholesaler.purchase.PurchaseDetail.line210_text') }}</h3>
        <div class="form-group">
          <label>{{ $t('wholesaler.purchase.PurchaseDetail.line212_text') }}</label>
          <div class="money-display">¥{{ formatMoney(order.totalAmount - order.paidAmount) }}</div>
        </div>
        <div class="form-group">
          <label>{{ $t('wholesaler.purchase.PurchaseDetail.line216_text') }}</label>
          <input type="number" v-model="paymentForm.amount" step="0.01" min="0.01" class="form-input" @blur="roundAmount" />
        </div>
        <div class="form-group">
          <label>{{ $t('wholesaler.purchase.PurchaseDetail.line220_text') }}</label>
          <select v-model="paymentForm.method" class="form-select">
            <option value="CASH">{{ $t('wholesaler.purchase.PurchaseDetail.cash') }}</option>
            <option value="CREDIT_CARD">{{ $t('wholesaler.purchase.PurchaseDetail.creditCard') }}</option>
            <option value="BANK_TRANSFER">{{ $t('wholesaler.purchase.PurchaseDetail.bankTransfer') }}</option>
            <option value="ONLINE_BANKING">{{ $t('wholesaler.purchase.PurchaseDetail.onlineBanking') }}</option>
          </select>
        </div>
        <div class="form-group">
          <label>{{ $t('wholesaler.purchase.PurchaseDetail.line202_text') }}</label>
          <input type="file" @change="uploadProof" accept="image/*" class="form-input" />
          <img v-if="paymentForm.proofUrl" :src="getImageUrl(paymentForm.proofUrl)" class="proof-preview" />
        </div>
        <div class="form-group">
          <label>{{ $t('wholesaler.purchase.PurchaseDetail.line234_text') }}</label>
          <textarea v-model="paymentForm.remark" class="form-textarea" rows="2"></textarea>
        </div>
        <div class="modal-actions">
          <button class="btn-cancel" @click="closePaymentDialog">{{ $t('wholesaler.purchase.PurchaseDetail.line238_text') }}</button>
          <button class="btn-confirm" @click="submitPayment">{{ $t('wholesaler.purchase.PurchaseDetail.line288_text') }}</button>
        </div>
      </div>
    </div>

    <!-- 退款弹窗 -->
    <div v-if="showRefundDialog" class="modal-overlay modal-show" @click="closeRefundDialog">
      <div class="modal-content" @click.stop>
        <h3>{{ $t('wholesaler.purchase.PurchaseDetail.refundRecord') }}</h3>
        
        <div class="form-group">
          <label class="checkbox-label">
            <input type="checkbox" v-model="refundForm.isReceived" @change="toggleRefundForm" />
            <span>{{ $t('wholesaler.purchase.PurchaseDetail.received') }}</span>
          </label>
        </div>

        <div v-if="refundForm.isReceived">
          <div class="form-group">
            <label>{{ $t('wholesaler.purchase.PurchaseDetail.refundType') }} *</label>
            <select v-model="refundForm.refundType" class="form-select" required>
              <option value="ORIGINAL">{{ $t('wholesaler.purchase.PurchaseDetail.originalReturn') }}</option>
              <option value="CASH">{{ $t('wholesaler.purchase.PurchaseDetail.cash') }}</option>
              <option value="CREDIT_CARD">{{ $t('wholesaler.purchase.PurchaseDetail.creditCard') }}</option>
              <option value="BANK_TRANSFER">{{ $t('wholesaler.purchase.PurchaseDetail.transfer') }}</option>
              <option value="PAYNOW">PayNow</option>
            </select>
          </div>

          <div class="form-group">
            <label>{{ $t('wholesaler.purchase.PurchaseDetail.refundAmount') }} *</label>
            <input type="number" v-model="refundForm.amount" step="0.01" min="0.01" class="form-input" required />
            <small class="form-hint">{{ $t('wholesaler.purchase.PurchaseDetail.defaultFull') }}: ¥{{ formatMoney(currentReturnOrder.totalRefundAmount) }}</small>
          </div>

          <div class="form-group">
            <label>{{ $t('wholesaler.purchase.PurchaseDetail.line202_text') }}</label>
            <input type="file" @change="uploadRefundProof" accept="image/*" class="form-input" />
            <img v-if="refundForm.proofUrl" :src="getImageUrl(refundForm.proofUrl)" class="proof-preview" />
          </div>

          <div class="form-group">
            <label>{{ $t('wholesaler.purchase.PurchaseDetail.line234_text') }}</label>
            <textarea v-model="refundForm.remark" class="form-textarea" rows="2"></textarea>
          </div>
        </div>

        <div class="modal-actions">
          <button class="btn-cancel" @click="closeRefundDialog">{{ $t('wholesaler.purchase.PurchaseDetail.line238_text') }}</button>
          <button class="btn-confirm" @click="submitRefund">{{ $t('wholesaler.purchase.PurchaseDetail.line288_text') }}</button>
        </div>
      </div>
    </div>

    <!-- 退货弹窗 -->
    <div v-if="showReturnDialog" class="modal-overlay modal-show" @click="closeReturnDialog">
      <div class="modal-content modal-large" @click.stop>
        <h3>{{ $t('wholesaler.purchase.PurchaseDetail.afterSalesReturn') }} - {{ order.purchaseNo }}</h3>
        
        <div class="return-section">
          <h4>{{ $t('wholesaler.purchase.PurchaseDetail.selectReturnProducts') }}</h4>
          <div class="return-items">
            <div v-for="(item, index) in returnForm.items" :key="index" class="return-item">
              <label class="checkbox-label">
                <input type="checkbox" v-model="item.selected" @change="updateReturnTotal" />
                <span>{{ item.productName }}</span>
              </label>
              <div class="item-details" v-if="item.selected">
                <div class="item-info">
                  <span>{{ $t('wholesaler.purchase.PurchaseDetail.purchaseQuantity') }}: {{ item.quantity }}{{ item.unit }}</span>
                </div>
                <div class="return-quantity">
                  <label>{{ $t('wholesaler.purchase.PurchaseDetail.returnQuantity') }}:</label>
                  <input type="number" v-model.number="item.returnQuantity" min="0.01" :max="item.quantity" step="0.01" class="quantity-input" @change="updateReturnTotal" />
                  <span>{{ item.unit }}</span>
                </div>
                <div class="return-price">
                  <label>{{ $t('wholesaler.purchase.PurchaseDetail.unitPrice') }}:</label>
                  <input type="number" v-model.number="item.unitPrice" min="0" step="0.01" class="price-input" @change="onPriceChange(item)" />
                </div>
                <div class="return-subtotal">
                  <label>{{ $t('wholesaler.purchase.PurchaseDetail.subtotal') }}:</label>
                  <input type="number" v-model.number="item.subtotal" min="0" step="0.01" class="subtotal-input" @change="onSubtotalChange(item)" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="return-section">
          <h4>{{ $t('wholesaler.purchase.PurchaseDetail.returnReason') }} *</h4>
          <select v-model="returnForm.reasonCode" class="form-select">
            <option value="">{{ $t('wholesaler.purchase.PurchaseDetail.selectReason') }}</option>
            <option value="QUALITY_ISSUE">{{ $t('wholesaler.purchase.PurchaseDetail.qualityIssue') }}</option>
            <option value="WRONG_ITEM">{{ $t('wholesaler.purchase.PurchaseDetail.wrongItem') }}</option>
            <option value="WRONG_QUANTITY">{{ $t('wholesaler.purchase.PurchaseDetail.wrongQuantity') }}</option>
            <option value="DAMAGED">{{ $t('wholesaler.purchase.PurchaseDetail.damaged') }}</option>
            <option value="EXPIRED">{{ $t('wholesaler.purchase.PurchaseDetail.expired') }}</option>
            <option value="SPEC_MISMATCH">{{ $t('wholesaler.purchase.PurchaseDetail.specMismatch') }}</option>
            <option value="OTHER">{{ $t('wholesaler.purchase.PurchaseDetail.other') }}</option>
          </select>
        </div>

        <div class="return-section">
          <h4>{{ $t('wholesaler.purchase.PurchaseDetail.returnDescription') }} *</h4>
          <textarea v-model="returnForm.reasonText" class="form-textarea" rows="3" :placeholder="$t('wholesaler.purchase.PurchaseDetail.describeReturnReason')"></textarea>
        </div>

        <div class="return-section">
          <h4>{{ $t('wholesaler.purchase.PurchaseDetail.uploadProof') }}</h4>
          <input type="file" @change="uploadReturnProof" accept="image/*" multiple class="form-input" />
          <div class="proof-list" v-if="returnForm.evidenceUrls.length > 0">
            <img v-for="(url, index) in returnForm.evidenceUrls" :key="index" :src="getImageUrl(url)" class="proof-thumb" />
          </div>
        </div>

        <div class="return-section">
          <h4>{{ $t('wholesaler.purchase.PurchaseDetail.refundInfo') }}</h4>
          <div class="refund-info">
            <div class="info-row">
              <span>{{ $t('wholesaler.purchase.PurchaseDetail.returnProductAmount') }}:</span>
              <span>¥{{ formatMoney(returnForm.totalAmount) }}</span>
            </div>
            <div class="info-row">
              <span>{{ $t('wholesaler.purchase.PurchaseDetail.refundAmount') }}:</span>
              <input type="number" v-model.number="returnForm.totalRefundAmount" step="0.01" min="0" class="refund-amount-input" />
            </div>
            <div class="info-row">
              <span>{{ $t('wholesaler.purchase.PurchaseDetail.refundMethod') }}:</span>
              <span>{{ getRefundMethodText(order.paymentMethod) }}</span>
            </div>
            <div class="info-row">
              <label class="checkbox-label">
                <input type="checkbox" v-model="returnForm.refundReceived" />
                <span>{{ $t('wholesaler.purchase.PurchaseDetail.receivedRefund') }}</span>
              </label>
            </div>
            <template v-if="returnForm.refundReceived">
              <div class="info-row">
                <span>{{ $t('wholesaler.purchase.PurchaseDetail.refundType') }}:</span>
                <select v-model="returnForm.refundType" class="form-select">
                  <option value="ORIGINAL">{{ $t('wholesaler.purchase.PurchaseDetail.originalReturn') }}</option>
                  <option value="CASH">{{ $t('wholesaler.purchase.PurchaseDetail.cash') }}</option>
                  <option value="CREDIT_CARD">{{ $t('wholesaler.purchase.PurchaseDetail.creditCard') }}</option>
                  <option value="BANK_TRANSFER">{{ $t('wholesaler.purchase.PurchaseDetail.transfer') }}</option>
                  <option value="PAYNOW">PayNow</option>
                </select>
              </div>
              <div class="info-row">
                <span>{{ $t('wholesaler.purchase.PurchaseDetail.receivedAmount') }}:</span>
                <input type="number" v-model.number="returnForm.refundAmount" step="0.01" min="0" class="refund-amount-input" />
              </div>
              <div class="info-row">
                <span>{{ $t('wholesaler.purchase.PurchaseDetail.receivedProof') }}:</span>
                <input type="file" @change="uploadRefundProof" accept="image/*" class="form-input" />
              </div>
              <div class="proof-list" v-if="returnForm.refundProofUrl">
                <img :src="getImageUrl(returnForm.refundProofUrl)" class="proof-thumb" />
              </div>
            </template>
          </div>
        </div>

        <div class="modal-actions">
          <button class="btn-cancel" @click="closeReturnDialog">{{ $t('wholesaler.purchase.PurchaseDetail.line238_text') }}</button>
          <button class="btn-confirm" @click="submitReturn" :disabled="!canSubmitReturn">{{ $t('wholesaler.purchase.PurchaseDetail.submitReturnApplication') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getImageUrl } from '@/utils/image';
import {
  getPurchaseOrderDetail,
  confirmPurchaseOrder,
  receivePurchaseOrder,
  cancelPurchaseOrder,
  payPurchaseOrder
} from '@/api/purchase'
import { createPurchaseReturn, getReturnsByPurchaseId, approvePurchaseReturn, rejectPurchaseReturn, receivePurchaseReturn, refundPurchaseReturn, cancelPurchaseReturn } from '@/api/purchaseReturn'
import { getWholesalerId } from '@/utils/wholesaler'

export default {
  name: 'PurchaseDetail',

  data() {
    return {
      order: null,
      returnOrders: [],
      showPaymentDialog: false,
      paymentForm: {
        amount: 0,
        method: 'CASH',
        remark: '',
        proofUrl: ''
      },
      showReturnDialog: false,
      returnForm: {
        items: [],
        reasonCode: '',
        reasonText: '',
        evidenceUrls: [],
        totalAmount: 0,
        gstAmount: 0,
        totalRefundAmount: 0,
        refundReceived: false,
        refundType: 'ORIGINAL',
        refundAmount: 0,
        refundProofUrl: ''
      },
      showRefundDialog: false,
      currentReturnOrder: null,
      refundForm: {
        isReceived: true,
        refundType: 'ORIGINAL',
        amount: 0,
        proofUrl: '',
        remark: ''
      }
    }
  },

  computed: {
    wholesalerId() {
      return getWholesalerId()
    },
    orderId() {
      return this.$route.params.id
    },
    // 累计退货金额（只统计已退款的）
    totalRefundAmount() {
      if (!this.returnOrders || this.returnOrders.length === 0) return 0
      return this.returnOrders
        .filter(ret => ret.status === 'REFUNDED' || ret.status === 'COMPLETED')
        .reduce((sum, ret) => sum + (ret.totalRefundAmount || 0), 0)
    },
    canSubmitReturn() {
      const hasSelected = this.returnForm.items.some(item => item.selected && item.returnQuantity > 0)
      return hasSelected && this.returnForm.reasonCode && this.returnForm.reasonText
    },
    totalSubtotal() {
      if (!this.order || !this.order.items) return 0
      return this.order.items.reduce((sum, item) => sum + (item.subtotal || 0), 0)
    },
    totalTaxAmount() {
      if (!this.order || !this.order.items) return 0
      return this.order.items.reduce((sum, item) => sum + (item.taxAmount || 0), 0)
    },
    totalWithTaxAmount() {
      if (!this.order || !this.order.items) return 0
      return this.order.items.reduce((sum, item) => sum + (item.totalWithTax || 0), 0)
    }
  },

  created() {
    this.loadDetail()
  },

  methods: {
    getImageUrl,
    isFullyRefunded(ret) {
      console.log('[isFullyRefunded] ret:', ret)
      console.log('[isFullyRefunded] totalRefundAmount:', ret.totalRefundAmount)
      console.log('[isFullyRefunded] refundRecords:', ret.refundRecords)
      if (!ret.totalRefundAmount || ret.totalRefundAmount <= 0) {
        console.log('[isFullyRefunded] 返回 false: totalRefundAmount 无效')
        return false
      }
      // 直接从退款流水汇总
      if (!ret.refundRecords || ret.refundRecords.length === 0) {
        console.log('[isFullyRefunded] 返回 false: refundRecords 为空')
        return false
      }
      const totalRefunded = ret.refundRecords.reduce((acc, record) => {
        return acc + (parseFloat(record.amount) || 0)
      }, 0)
      console.log('[isFullyRefunded] totalRefunded:', totalRefunded)
      console.log('[isFullyRefunded] 判断结果:', totalRefunded >= ret.totalRefundAmount)
      return totalRefunded >= ret.totalRefundAmount
    },
    async loadDetail() {
      try {
        const res = await getPurchaseOrderDetail(this.orderId, this.wholesalerId)
        this.order = res
        // 加载售后单列表
        this.loadReturnOrders()
      } catch (e) {
        console.error('加载详情失败', e)
        this.$toast.error('加载失败')
      }
    },

    async loadReturnOrders() {
      try {
        const res = await getReturnsByPurchaseId(this.orderId, this.wholesalerId)
        this.returnOrders = res || []
      } catch (e) {
        console.error('加载售后单失败', e)
      }
    },

    async confirmOrder() {
      if (!confirm('确认此进货单？')) return
      try {
        await confirmPurchaseOrder(this.orderId, this.wholesalerId)
        this.$toast.success('已确认')
        this.loadDetail()
      } catch (e) {
        this.$toast.error('操作失败')
      }
    },

    async receiveOrder() {
      if (!confirm('确认收货？收货后库存将自动增加。')) return
      try {
        const userId = localStorage.getItem('user_id')
        await receivePurchaseOrder(this.orderId, this.wholesalerId, userId)
        this.$toast.success('收货成功，库存已更新')
        this.loadDetail()
      } catch (e) {
        this.$toast.error('操作失败')
      }
    },

    async cancelOrder() {
      if (!confirm('确认取消此进货单？')) return
      try {
        await cancelPurchaseOrder(this.orderId, this.wholesalerId)
        this.$toast.success('已取消')
        this.loadDetail()
      } catch (e) {
        this.$toast.error('操作失败')
      }
    },

    openPaymentDialog() {
      this.paymentForm.amount = Math.round((this.order.totalAmount - this.order.paidAmount) * 100) / 100
      this.paymentForm.method = 'CASH'
      this.paymentForm.remark = ''
      this.paymentForm.proofUrl = ''
      this.showPaymentDialog = true
    },
    roundAmount() {
      if (this.paymentForm.amount) {
        this.paymentForm.amount = Math.round(this.paymentForm.amount * 100) / 100
      }
    },

    closePaymentDialog() {
      this.showPaymentDialog = false
    },

    async submitPayment() {
      if (!this.paymentForm.amount || this.paymentForm.amount <= 0) {
        this.$toast.error('请输入支付金额')
        return
      }
      if (!this.paymentForm.proofUrl) {
        this.$toast.error('请上传支付凭证')
        return
      }
      try {
        const operatorId = localStorage.getItem('user_id')
        await payPurchaseOrder(this.orderId, {
          amount: this.paymentForm.amount,
          paymentMethod: this.paymentForm.method,
          remark: this.paymentForm.remark,
          proofUrl: this.paymentForm.proofUrl
        }, this.wholesalerId, operatorId)
        this.$toast.success('支付成功')
        this.closePaymentDialog()
        this.loadDetail()
      } catch (e) {
        console.error('支付失败', e)
        this.$toast.error('支付失败: ' + (e.response?.data?.message || e.message))
      }
    },

    async uploadProof(e) {
      const file = e.target.files[0]
      if (!file) return
      try {
        const { uploadFile } = require('@/services/api')
        const res = await uploadFile('/files/upload-image', file)
        this.paymentForm.proofUrl = res.url
        this.$toast.success('凭证上传成功')
      } catch (err) {
        console.error('上传失败', err)
        this.$toast.error('上传失败: ' + err.message)
      }
    },

    goBack() {
      this.$router.go(-1)
    },

    getStatusClass(status) {
      const map = {
        DRAFT: 'draft',
        CONFIRMED: 'pending',
        RECEIVED: 'success',
        CANCELLED: 'cancelled'
      }
      return map[status] || ''
    },

    getPaymentClass(status) {
      if (status === 'PAID') return 'paid'
      if (status === 'PARTIAL') return 'partial'
      return 'unpaid'
    },

    getPaymentLabel(status) {
      const map = { 
        UNPAID: this.$t('wholesaler.purchase.PurchaseDetail.unpaid'), 
        PARTIAL: this.$t('wholesaler.purchase.PurchaseDetail.partial'), 
        PAID: this.$t('wholesaler.purchase.PurchaseDetail.paid') 
      }
      return map[status] || status
    },

    getMethodLabel(method) {
      const map = { 
        CASH: this.$t('wholesaler.purchase.PurchaseDetail.cash'), 
        TRANSFER: this.$t('wholesaler.purchase.PurchaseDetail.transfer'), 
        WECHAT: this.$t('wholesaler.purchase.PurchaseDetail.wechat'), 
        ALIPAY: this.$t('wholesaler.purchase.PurchaseDetail.alipay') 
      }
      return map[method] || method
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      return dateStr.replace('T', ' ').substring(0, 16)
    },

    formatMoney(value) {
      if (!value) return '0.00'
      return Number(value).toFixed(2)
    },

    getTaxLabel(item) {
      if (!item.taxType || item.taxType === 'NONE') {
        return '无税'
      }
      const rate = Math.round((item.taxRate || 0) * 100)
      if (item.taxType === 'INCLUSIVE') {
        return `含税${rate}%`
      } else if (item.taxType === 'EXCLUSIVE') {
        return `+税${rate}%`
      }
      return '无税'
    },

    getTaxClass(item) {
      if (!item.taxType || item.taxType === 'NONE') {
        return 'tax-none'
      }
      if (item.taxType === 'INCLUSIVE') {
        return 'tax-inclusive'
      } else if (item.taxType === 'EXCLUSIVE') {
        return 'tax-exclusive'
      }
      return 'tax-none'
    },

    createReturn() {
      this.openReturnDialog()
    },

    openReturnDialog() {
      // 初始化退货商品列表，默认全选全退
      this.returnForm.items = this.order.items.map(item => {
        // 使用含税单价（subtotal 现在是含税的），保留4位小数
        const taxInclusiveUnitPrice = Math.round((item.subtotal / item.quantity) * 10000) / 10000
        
        return {
          ...item,
          selected: true,
          returnQuantity: item.quantity,
          unitPrice: taxInclusiveUnitPrice,
          subtotal: item.subtotal  // 直接使用含税小计
        }
      })
      this.returnForm.reasonCode = ''
      this.returnForm.reasonText = ''
      this.returnForm.evidenceUrls = []
      // 计算默认全额（使用含税小计）
      const totalAmount = this.order.items.reduce((sum, item) => sum + item.subtotal, 0)
      this.returnForm.totalAmount = Math.round(totalAmount * 100) / 100
      this.returnForm.gstAmount = 0
      this.returnForm.totalRefundAmount = this.returnForm.totalAmount
      this.returnForm.refundReceived = true
      this.returnForm.refundType = 'ORIGINAL'
      this.returnForm.refundAmount = Math.round(this.returnForm.totalAmount * 100) / 100
      this.returnForm.refundProofUrl = ''
      this.showReturnDialog = true
    },

    closeReturnDialog() {
      this.showReturnDialog = false
    },

    updateReturnTotal() {
      let total = 0
      this.returnForm.items.forEach(item => {
        if (item.selected && item.returnQuantity > 0) {
          // 计算含税小计（unitPrice 已经是含税单价）
          item.subtotal = Math.round(item.returnQuantity * item.unitPrice * 100) / 100
          total += item.subtotal
        }
      })
      // 对总金额四舍五入到2位小数
      this.returnForm.totalAmount = Math.round(total * 100) / 100
      this.returnForm.totalRefundAmount = this.returnForm.totalAmount
    },

    onPriceChange(item) {
      if (item.returnQuantity > 0 && item.unitPrice >= 0) {
        item.subtotal = Math.round(item.returnQuantity * item.unitPrice * 100) / 100
        this.updateReturnTotal()
      }
    },

    onSubtotalChange(item) {
      if (item.returnQuantity > 0 && item.subtotal >= 0) {
        item.unitPrice = Math.round((item.subtotal / item.returnQuantity) * 100) / 100
        this.updateReturnTotal()
      }
    },

    async uploadReturnProof(e) {
      const files = Array.from(e.target.files)
      if (!files || files.length === 0) return
      
      try {
        const { uploadFile } = require('@/services/api')
        for (const file of files) {
          const res = await uploadFile('/files/upload-image', file)
          this.returnForm.evidenceUrls.push(res.url)
        }
        this.$toast.success('凭证上传成功')
      } catch (err) {
        console.error('上传失败', err)
        this.$toast.error('上传失败: ' + err.message)
      }
    },

    async submitReturn() {
      // 验证
      const selectedItems = this.returnForm.items.filter(item => item.selected && item.returnQuantity > 0)
      if (selectedItems.length === 0) {
        this.$toast.error('请选择退货商品')
        return
      }
      if (!this.returnForm.reasonCode) {
        this.$toast.error('请选择退货原因')
        return
      }
      if (!this.returnForm.reasonText) {
        this.$toast.error('请填写退货说明')
        return
      }

      try {
        const data = {
          purchaseId: this.order.id,
          reasonCode: this.returnForm.reasonCode,
          reasonText: this.returnForm.reasonText,
          evidenceUrls: JSON.stringify(this.returnForm.evidenceUrls),
          totalRefundAmount: this.returnForm.totalRefundAmount,
          items: selectedItems.map(item => ({
            purchaseItemId: item.id,
            productId: item.productId,
            returnQuantity: item.returnQuantity,
            unitPrice: item.unitPrice,
            subtotal: item.subtotal
          }))
        }

        // 已收到退款，附带退款流水信息
        if (this.returnForm.refundReceived) {
          data.refundReceived = true
          data.refundType = this.returnForm.refundType
          data.refundAmount = this.returnForm.refundAmount || this.returnForm.totalRefundAmount
          data.refundProofUrl = this.returnForm.refundProofUrl
        }

        const userId = localStorage.getItem('user_id')
        await createPurchaseReturn(data, this.wholesalerId, userId)
        this.$toast.success('退货申请提交成功')
        this.closeReturnDialog()
        this.loadDetail()
      } catch (e) {
        console.error('提交退货失败', e)
        this.$toast.error('提交失败: ' + (e.response?.data?.message || e.message))
      }
    },

    async uploadRefundProof(e) {
      const file = e.target.files[0]
      if (!file) return
      try {
        const { uploadFile } = require('@/services/api')
        const res = await uploadFile('/files/upload-image', file)
        this.returnForm.refundProofUrl = res.url
        this.$toast.success('收款凭证上传成功')
      } catch (err) {
        console.error('上传失败', err)
        this.$toast.error('上传失败: ' + err.message)
      }
    },

    getRefundMethodText(paymentMethod) {
      const map = {
        CASH: this.$t('wholesaler.purchase.PurchaseDetail.cashReturn'),
        CREDIT_CARD: this.$t('wholesaler.purchase.PurchaseDetail.creditCardReturn'),
        BANK_TRANSFER: this.$t('wholesaler.purchase.PurchaseDetail.bankTransferReturn'),
        ONLINE_BANKING: this.$t('wholesaler.purchase.PurchaseDetail.onlineBankingReturn')
      }
      return map[paymentMethod] || this.$t('wholesaler.purchase.PurchaseDetail.originalReturn')
    },

    getReturnStatusLabel(status) {
      const map = {
        PENDING_REVIEW: this.$t('wholesaler.purchase.PurchaseDetail.pendingReview'),
        REVIEWED: this.$t('wholesaler.purchase.PurchaseDetail.reviewed'),
        RECEIVED: this.$t('wholesaler.purchase.PurchaseDetail.received'),
        REFUNDED: this.$t('wholesaler.purchase.PurchaseDetail.refunded'),
        COMPLETED: this.$t('wholesaler.purchase.PurchaseDetail.completed'),
        REJECTED: this.$t('wholesaler.purchase.PurchaseDetail.rejected'),
        CANCELLED: this.$t('wholesaler.purchase.PurchaseDetail.cancelled')
      }
      return map[status] || status
    },

    getReasonLabel(code) {
      const map = {
        QUALITY_ISSUE: this.$t('wholesaler.purchase.PurchaseDetail.qualityIssue'),
        WRONG_ITEM: this.$t('wholesaler.purchase.PurchaseDetail.wrongItem'),
        WRONG_QUANTITY: this.$t('wholesaler.purchase.PurchaseDetail.wrongQuantity'),
        DAMAGED: this.$t('wholesaler.purchase.PurchaseDetail.damaged'),
        EXPIRED: this.$t('wholesaler.purchase.PurchaseDetail.expired'),
        SPEC_MISMATCH: this.$t('wholesaler.purchase.PurchaseDetail.specMismatch'),
        OTHER: this.$t('wholesaler.purchase.PurchaseDetail.other')
      }
      return map[code] || code
    },

    getEvidenceUrls(evidenceUrls) {
      if (!evidenceUrls) return []
      if (Array.isArray(evidenceUrls)) return evidenceUrls
      if (typeof evidenceUrls === 'string') {
        try {
          const parsed = JSON.parse(evidenceUrls)
          return Array.isArray(parsed) ? parsed : []
        } catch (e) {
          return []
        }
      }
      return []
    },

    previewImage(url) {
      window.open(this.getImageUrl(url), '_blank')
    },

    async approveReturn(ret) {
      if (!confirm(`确认通过退货单 ${ret.returnNo}？`)) return
      try {
        const userId = localStorage.getItem('user_id')
        await approvePurchaseReturn(ret.id, this.wholesalerId, userId, '')
        this.$toast.success('审核通过')
        this.loadDetail()
      } catch (e) {
        console.error('审核失败', e)
        this.$toast.error('审核失败: ' + (e.response?.data?.message || e.message))
      }
    },

    async rejectReturn(ret) {
      const reason = prompt('请输入拒绝原因：')
      if (reason === null) return
      if (!reason.trim()) {
        this.$toast.error('请输入拒绝原因')
        return
      }
      try {
        const userId = localStorage.getItem('user_id')
        await rejectPurchaseReturn(ret.id, this.wholesalerId, userId, reason)
        this.$toast.success('已拒绝')
        this.loadDetail()
      } catch (e) {
        console.error('审核失败', e)
        this.$toast.error('审核失败: ' + (e.response?.data?.message || e.message))
      }
    },

    async receiveReturn(ret) {
      if (!confirm(`确认收到退货单 ${ret.returnNo}？`)) return
      try {
        const userId = localStorage.getItem('user_id')
        await receivePurchaseReturn(ret.id, this.wholesalerId, userId)
        this.$toast.success('已确认收货')
        this.loadDetail()
      } catch (e) {
        console.error('确认收货失败', e)
        this.$toast.error('确认收货失败: ' + (e.response?.data?.message || e.message))
      }
    },

    async refundReturn(ret) {
      this.currentReturnOrder = ret
      this.refundForm.amount = Math.round((ret.totalRefundAmount || 0) * 100) / 100
      this.refundForm.status = 'completed'
      this.refundForm.refundType = 'ORIGINAL'
      this.refundForm.proofUrl = ''
      this.refundForm.remark = ''
      this.showRefundDialog = true
    },

    closeRefundDialog() {
      this.showRefundDialog = false
      this.currentReturnOrder = null
    },

    toggleRefundForm() {
      // 切换已收款状态时，重置表单
      if (!this.refundForm.isReceived) {
        this.refundForm.refundType = 'ORIGINAL'
        this.refundForm.amount = 0
        this.refundForm.proofUrl = ''
        this.refundForm.remark = ''
      } else {
        // 勾选已收款时，默认填充退货单金额
        this.refundForm.amount = Math.round((this.currentReturnOrder?.totalRefundAmount || 0) * 100) / 100
      }
    },

    async submitRefund() {
      if (!this.currentReturnOrder) return
      
      // 如果勾选了已收款，需要验证必填字段
      if (this.refundForm.isReceived) {
        if (!this.refundForm.amount || this.refundForm.amount <= 0) {
          this.$toast.error('请输入退款金额')
          return
        }
      }

      try {
        const userId = localStorage.getItem('user_id')
        // 根据 isReceived 确定状态
        const refundStatus = this.refundForm.isReceived ? 'completed' : 'pending'
        // 如果未收款，金额设为0
        const refundAmount = this.refundForm.isReceived ? this.refundForm.amount : 0
        
        await refundPurchaseReturn(
          this.currentReturnOrder.id,
          this.wholesalerId,
          userId,
          this.refundForm.remark,
          this.refundForm.proofUrl,
          refundAmount,
          this.refundForm.refundType,
          refundStatus
        )
        
        const statusText = this.refundForm.isReceived ? this.$t('wholesaler.purchase.PurchaseDetail.received') : this.$t('wholesaler.purchase.PurchaseDetail.pendingReceive')
        this.$toast.success(`${statusText}，退款流水已录入`)
        this.closeRefundDialog()
        this.loadDetail()
      } catch (e) {
        console.error('退款失败', e)
        this.$toast.error('退款失败: ' + (e.response?.data?.message || e.message))
      }
    },

    async uploadRefundProof(e) {
      const file = e.target.files[0]
      if (!file) return
      try {
        const { uploadFile } = require('@/services/api')
        const res = await uploadFile('/files/upload-image', file)
        this.refundForm.proofUrl = res.url
        this.$toast.success('凭证上传成功')
      } catch (err) {
        console.error('上传失败', err)
        this.$toast.error('上传失败: ' + err.message)
      }
    },

    async cancelReturn(ret) {
      if (!confirm(`确认取消退货单 ${ret.returnNo}？`)) return
      try {
        const userId = localStorage.getItem('user_id')
        await cancelPurchaseReturn(ret.id, this.wholesalerId, userId)
        this.$toast.success('已取消')
        this.loadDetail()
      } catch (e) {
        console.error('取消失败', e)
        this.$toast.error('取消失败: ' + (e.response?.data?.message || e.message))
      }
    }
  }
}
</script>

<style scoped>
.purchase-detail-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 100px;
}

.back-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: white;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  z-index: 100;
}

.back-btn {
  font-size: 20px;
  cursor: pointer;
  padding: 4px 8px;
}

.back-header h1 {
  flex: 1;
  font-size: 18px;
  margin: 0;
}

.main-content {
  padding: 16px;
}

.loading {
  text-align: center;
  padding: 60px;
  color: #999;
}

.status-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  margin-bottom: 16px;
  border-left: 4px solid #ddd;
}

.status-card.draft { border-left-color: #9e9e9e; }
.status-card.pending { border-left-color: #ff9800; }
.status-card.success { border-left-color: #4caf50; }
.status-card.cancelled { border-left-color: #f44336; }

.status-label {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 8px;
}

.order-no {
  font-size: 14px;
  color: #666;
}

.section {
  background: white;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}

.section-title {
  font-size: 16px;
  font-weight: bold;
  margin: 0 0 12px 0;
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.info-item {
  display: flex;
  flex-direction: column;
}

.info-item.full-width {
  grid-column: 1 / -1;
}

.info-item .label {
  font-size: 12px;
  color: #999;
  margin-bottom: 4px;
}

.info-item .value {
  font-size: 14px;
}

.items-list {
  margin-bottom: 12px;
}

.item-card {
  border: 1px solid #eee;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 8px;
}

.item-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.item-name {
  font-weight: bold;
}

.item-sku {
  font-size: 12px;
  color: #999;
}

.item-body {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.item-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: #666;
}

.item-info .info-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.item-subtotal {
  color: #ff6600;
  font-weight: bold;
  font-size: 16px;
}

.tax-amount {
  color: #999;
  font-size: 14px;
  margin-left: 8px;
}

.tax-total {
  color: #ff6600;
  font-weight: bold;
  margin-left: 8px;
}

.tax-summary {
  background: #f9f9f9;
  padding: 8px;
  border-radius: 4px;
  margin: 8px 0;
}

.summary {
  border-top: 1px solid #eee;
  padding-top: 12px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
}

.summary-row.total {
  font-size: 16px;
  font-weight: bold;
}

.summary-row.total span:last-child {
  color: #ff6600;
}

.payment-summary {
  margin-bottom: 16px;
}

.payment-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f5f5f5;
}

.money {
  font-weight: bold;
}

.money.paid { color: #4caf50; }
.money.unpaid { color: #f44336; }

.paid { color: #4caf50; }
.partial { color: #ff9800; }
.unpaid { color: #f44336; }

.payment-records h4 {
  font-size: 14px;
  margin: 12px 0 8px 0;
  color: #666;
}

.payment-record {
  background: #f9f9f9;
  border-radius: 4px;
  padding: 10px;
  margin-bottom: 8px;
}

.record-proof {
  margin-top: 8px;
}

.proof-image {
  max-width: 100px;
  max-height: 100px;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.2s;
}

.proof-image:hover {
  transform: scale(1.05);
  border-color: #409eff;
}

.refund-records {
  margin-top: 20px;
}

.refund-records h4 {
  font-size: 14px;
  margin: 12px 0 8px 0;
  color: #666;
}

.refund-record {
  background: #fff5f5;
  border-radius: 4px;
  padding: 10px;
  margin-bottom: 8px;
  border-left: 3px solid #f56c6c;
}

.refund-amount {
  color: #f56c6c !important;
}

.refund-no {
  font-size: 12px;
  color: #999;
}

.record-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
}

.record-header .amount {
  font-weight: bold;
  color: #ff6600;
}

.record-header .method {
  font-size: 12px;
  color: #666;
}

.record-footer {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #999;
}

.action-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  gap: 8px;
  padding: 16px;
  background: white;
  border-top: 1px solid #eee;
}

.action-btn {
  flex: 1;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 14px;
}

.action-btn.primary { background: #1976d2; color: white; border-color: #1976d2; }
.action-btn.success { background: #4caf50; color: white; border-color: #4caf50; }
.action-btn.warning { background: #ff9800; color: white; border-color: #ff9800; }
.action-btn.after-sale { background: #9c27b0; color: white; border-color: #9c27b0; }
.action-btn.danger { color: #f44336; border-color: #f44336; }

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
}

.modal-content h3 {
  margin: 0 0 16px 0;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 4px;
  font-size: 14px;
  color: #666;
}

.form-input, .form-select, .form-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

.money-display {
  font-size: 24px;
  font-weight: bold;
  color: #ff6600;
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
}

.btn-cancel, .btn-confirm {
  padding: 10px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-cancel {
  background: white;
  border: 1px solid #ddd;
}

.btn-confirm {
  background: #ff6600;
  color: white;
  border: none;
}

.proof-preview {
  max-width: 100%;
  max-height: 200px;
  margin-top: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

/* 退货弹窗样式 */
.modal-large {
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
}

.return-section {
  margin-bottom: 20px;
}

.return-section h4 {
  margin: 0 0 10px 0;
  font-size: 14px;
  color: #333;
}

.return-items {
  border: 1px solid #eee;
  border-radius: 4px;
  padding: 10px;
}

.return-item {
  padding: 10px 0;
  border-bottom: 1px solid #f5f5f5;
}

.return-item:last-child {
  border-bottom: none;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: 500;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.item-details {
  margin-top: 10px;
  padding: 10px;
  background: #f9f9f9;
  border-radius: 4px;
}

.item-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 13px;
  color: #666;
}

.return-quantity {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.return-quantity label {
  font-size: 13px;
  color: #666;
}

.quantity-input {
  width: 100px;
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.item-subtotal {
  font-size: 14px;
  color: #ff6600;
  font-weight: bold;
}

.refund-info {
  background: #f9f9f9;
  padding: 12px;
  border-radius: 4px;
}

.refund-info .info-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 14px;
}

.refund-info .info-row.total {
  border-top: 1px solid #ddd;
  margin-top: 8px;
  padding-top: 12px;
  font-weight: bold;
}

.refund-info .info-row .amount {
  color: #ff6600;
  font-size: 18px;
}

.refund-amount-input {
  width: 120px;
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  color: #ff6600;
  font-weight: bold;
  text-align: right;
}

.proof-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.proof-thumb {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.btn-confirm:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* 售后单列表样式 */
.return-orders-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.return-order-card {
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 12px;
  background: #fafafa;
}

.return-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.return-no {
  font-weight: bold;
  font-size: 14px;
  color: #333;
}

.return-status {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.return-status.status-pending_review {
  background: #fff3e0;
  color: #f57c00;
}

.return-status.status-reviewed {
  background: #e3f2fd;
  color: #1976d2;
}

.return-status.status-received {
  background: #f3e5f5;
  color: #7b1fa2;
}

.return-status.status-refunded {
  background: #e8f5e9;
  color: #388e3c;
}

.return-status.status-completed {
  background: #e8f5e9;
  color: #388e3c;
}

.return-status.status-rejected {
  background: #ffebee;
  color: #d32f2f;
}

.return-status.status-cancelled {
  background: #f5f5f5;
  color: #757575;
}

.return-body {
  margin-bottom: 8px;
}

.return-info,
.return-reason,
.return-time {
  font-size: 13px;
  color: #666;
  margin-bottom: 4px;
}

.return-review {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #ddd;
}

.review-info {
  font-size: 13px;
  color: #666;
  margin-bottom: 4px;
}

.review-info span {
  color: #333;
}

.return-actions {
  display: flex;
  gap: 8px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #eee;
}

.return-actions .action-btn {
  flex: none;
  padding: 6px 16px;
  font-size: 13px;
}

.refunded-tag {
  display: inline-block;
  padding: 6px 16px;
  font-size: 13px;
  background: #67c23a;
  color: white;
  border-radius: 4px;
}

.return-evidence {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #eee;
}

.evidence-label {
  font-size: 13px;
  color: #666;
  margin-bottom: 6px;
  display: block;
}

.evidence-images {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.evidence-thumb {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.2s;
}

.evidence-thumb:hover {
  transform: scale(1.05);
  border-color: #ff6600;
}
</style>
